"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

type Preference =
  "text-size-1" | "text-size-2" | "high-contrast" | "underlined-links" | "reduce-motion";
const storageKey = "sensemode-accessibility-preferences";

export function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState<Preference[] | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const restorePreferences = window.setTimeout(() => {
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey) ?? "[]") as Preference[];
        if (Array.isArray(stored))
          setPreferences(
            stored.filter((item): item is Preference =>
              [
                "text-size-1",
                "text-size-2",
                "high-contrast",
                "underlined-links",
                "reduce-motion",
              ].includes(item),
            ),
          );
      } catch {
        /* Ignore malformed local preference data. */
      }
    }, 0);
    return () => window.clearTimeout(restorePreferences);
  }, []);

  useEffect(() => {
    if (preferences === null) return;
    const root = document.documentElement;
    const known: Preference[] = [
      "text-size-1",
      "text-size-2",
      "high-contrast",
      "underlined-links",
      "reduce-motion",
    ];
    known.forEach((item) => root.classList.toggle(`a11y-${item}`, preferences.includes(item)));
    localStorage.setItem(storageKey, JSON.stringify(preferences));
  }, [preferences]);

  useEffect(() => {
    if (!open) return;
    const focusable = () =>
      panelRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), summary",
      );
    const initial = window.setTimeout(() => focusable()?.[0]?.focus(), 0);
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = Array.from(focusable() ?? []);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(initial);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function close() {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }
  function toggle(preference: Preference) {
    setPreferences((current) => {
      const active = current ?? [];
      return active.includes(preference)
        ? active.filter((item) => item !== preference)
        : [...active, preference];
    });
  }
  function textSize() {
    return preferences?.includes("text-size-2") ? 2 : preferences?.includes("text-size-1") ? 1 : 0;
  }
  function changeTextSize(direction: 1 | -1) {
    setPreferences((current) => {
      const active = current ?? [];
      const currentSize = active.includes("text-size-2")
        ? 2
        : active.includes("text-size-1")
          ? 1
          : 0;
      const next = Math.max(0, Math.min(2, currentSize + direction));
      return [
        ...active.filter((item) => item !== "text-size-1" && item !== "text-size-2"),
        ...(next === 2
          ? ["text-size-2" as Preference]
          : next === 1
            ? ["text-size-1" as Preference]
            : []),
      ];
    });
  }
  function reset() {
    setPreferences([]);
  }

  return (
    <div className="accessibility-widget">
      <button
        ref={triggerRef}
        type="button"
        className="accessibility-trigger"
        aria-label="פתיחת אפשרויות נגישות"
        aria-expanded={open}
        aria-controls="accessibility-panel"
        onClick={() => setOpen(true)}
      >
        <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
          <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="4" />
          <circle cx="32" cy="15" r="4" fill="currentColor" />
          <path d="M25 25h15l4 8H34l7 16h-7l-7-14-5 7h-7l7-12Z" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <aside
          ref={panelRef}
          id="accessibility-panel"
          className="accessibility-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="accessibility-title"
        >
          <div className="accessibility-panel__top">
            <div>
              <p className="eyebrow">נגישות</p>
              <h2 id="accessibility-title">התאמת התצוגה</h2>
            </div>
            <button
              type="button"
              className="icon-close"
              onClick={close}
              aria-label="סגירת חלונית נגישות"
            >
              ×
            </button>
          </div>
          <p className="accessibility-panel__intro">
            אפשרויות אלה נועדו להקל על הקריאה באתר ואינן מחליפות את הנגישות המובנית בו.
          </p>
          <div className="accessibility-options">
            <div className="text-size-control">
              <span id="text-size-label">גודל הטקסט</span>
              <div role="group" aria-labelledby="text-size-label">
                <button
                  type="button"
                  onClick={() => changeTextSize(-1)}
                  disabled={textSize() === 0}
                  aria-label="הקטנת הטקסט"
                >
                  א−
                </button>
                <output aria-live="polite">{["רגיל", "מוגדל", "מוגדל מאוד"][textSize()]}</output>
                <button
                  type="button"
                  onClick={() => changeTextSize(1)}
                  disabled={textSize() === 2}
                  aria-label="הגדלת הטקסט"
                >
                  א+
                </button>
              </div>
            </div>
            <button
              type="button"
              aria-pressed={Boolean(preferences?.includes("high-contrast"))}
              onClick={() => toggle("high-contrast")}
            >
              ניגודיות גבוהה
            </button>
            <button
              type="button"
              aria-pressed={Boolean(preferences?.includes("underlined-links"))}
              onClick={() => toggle("underlined-links")}
            >
              הדגשת קישורים
            </button>
            <button
              type="button"
              aria-pressed={Boolean(preferences?.includes("reduce-motion"))}
              onClick={() => toggle("reduce-motion")}
            >
              צמצום תנועה והנפשות
            </button>
          </div>
          <details className="keyboard-help">
            <summary>עזרה בשימוש במקלדת</summary>
            <p>
              עברו בין רכיבים עם Tab, חזרו עם Shift+Tab, הפעילו כפתורים עם Enter או רווח, וסגרו
              חלונית זו עם Esc.
            </p>
          </details>
          <div className="accessibility-panel__bottom">
            <div>
              <Link href="/accessibility" onClick={close}>
                להצהרת הנגישות
              </Link>
              <Link href="/contact" onClick={close}>
                פנו אלינו לסיוע
              </Link>
            </div>
            <button type="button" className="text-button" onClick={reset}>
              איפוס התאמות
            </button>
          </div>
        </aside>
      )}
    </div>
  );
}
